/* eslint-disable @typescript-eslint/no-explicit-any */
import API from '@/APIs/API';
import AthenaAPI from '@/APIs/AthenaAPI';
import {observer} from 'mobx-react-lite';
import {useSession} from 'next-auth/react';
import {useEffect, useState} from 'react';
import {useMutation} from 'react-query';
import {useHistory} from 'react-router-dom';
import {useRealtime} from '../App/RealtimeProvider';
import RootStore from '../Core/mobx/RootStore';
import {
    ConnectedCall,
    DialingCall,
    LoadingComponent,
    NoCallRecordFound,
    NoClientRecordFound,
} from './LiveCallComponents';
import {PERSONA_TOOLTIP_MAP} from './utils/personaTooltipMap';

const getUserData = (currentCall: any, fieldName: string, fallback: any = 'Unknown') => {
    try {
        // Helper function to search an object recursively
        const findValueByKey = (obj: any, targetKey: string): any => {
            if (!obj || typeof obj !== 'object') return undefined;

            // Direct match
            if (obj.hasOwnProperty(targetKey)) return obj[targetKey];

            // Search in nested objects
            for (const key in obj) {
                const value = obj[key];
                if (typeof value === 'object') {
                    const found = findValueByKey(value, targetKey);
                    if (found !== undefined) return found;
                }
            }
            return undefined;
        };

        // Search in walkthroughData first
        const dataValue = findValueByKey(currentCall?.walkthroughData, fieldName);

        if (dataValue !== undefined) return dataValue;

        // If not found, search in walkthroughState
        const stateValue = findValueByKey(currentCall?.walkthroughState, fieldName);
        if (stateValue !== undefined) return stateValue;

        return fallback;
    } catch (error) {
        return fallback;
    }
};

export const Live = observer(() => {
    const session = useSession();
    const {messages, currentCall, isLoading, setCurrentCall} = RootStore.liveTabStore;
    const {notes} = RootStore.assistStore;
    const {subscriptionStatus, connectionStatus} = useRealtime();
    const [showRefreshButton, setShowRefreshButton] = useState(
        connectionStatus.toLowerCase() === 'disconnected' || subscriptionStatus.toLowerCase() === 'disconnected',
    );

    const history = useHistory();

    const logCallConnectedMutation = useMutation({
        mutationFn: ({
            agent,
            clientEmail,
            clientPhoneNumber,
            callId,
        }: {
            agent: string;
            clientEmail: string;
            clientPhoneNumber: string;
            callId: string;
        }) =>
            API.LogToMixPanel('agent-live-tab-call-connected-view', {
                agent,
                clientEmail,
                clientPhoneNumber,
                callId,
            }),
    });

    const logLiveTabVisitMutation = useMutation({
        mutationFn: ({agent}: {agent: string}) =>
            API.LogToMixPanel('agent-live-tab-viewed', {
                agent,
            }),
    });

    useEffect(() => {
        if (currentCall?.hookEvent === 'CALL_ANSWERED' && session.data?.user?.email && currentCall) {
            logCallConnectedMutation.mutate({
                agent: session.data.user.email,
                clientEmail: currentCall.clientEmail,
                clientPhoneNumber: currentCall.clientPhoneNumber,
                callId: currentCall.callId,
            });
        }
    }, [currentCall, session.data?.user?.email]);

    useEffect(() => {
        if (session.data?.user?.email) {
            logLiveTabVisitMutation.mutate({
                agent: session.data.user.email,
            });
        }
    }, []);

    useEffect(() => {
        setShowRefreshButton(
            connectionStatus.toLowerCase() === 'disconnected' || subscriptionStatus.toLowerCase() === 'disconnected',
        );
    }, [connectionStatus, subscriptionStatus]);

    useEffect(() => {
        RootStore.assistStore.resetStore();
    }, []);

    const handleRefresh = () => {
        window.location.reload();
    };

    const onNewForm = async () => {
        const userIdData = currentCall?.walkthroughData?.userIdData as {email: string};

        if (!userIdData?.email) return;

        const {email} = userIdData;

        const resp = await AthenaAPI.getContacts({search: email, page: 1, limit: 1});

        if (!resp || !resp.success) {
            return;
        }

        const [contact] = resp.data;

        if (contact) {
            await RootStore.agentPortalStore.startNewForm({...contact, notes});
            history.push('/agent/newform');
        }
    };

    const onHubspot = () => {
        window.open('https://app.hubspot.com/', '_blank');
    };

    const handleClearCall = () => {
        setCurrentCall(undefined);
    };

    useEffect(() => {
        if (!currentCall && !messages.length && session.data?.user?.email) {
            RootStore.liveTabStore.checkActiveCall(session.data.user.email);
        }
    }, [currentCall, messages.length]);

    // Show loading component while checking for active calls
    if (isLoading && !currentCall && !messages.length) {
        return (
            <LoadingComponent
                connectionStatus={connectionStatus}
                subscriptionStatus={subscriptionStatus}
                showRefreshButton={showRefreshButton}
                handleRefresh={handleRefresh}
            />
        );
    }

    if (!currentCall || (currentCall?.hookEvent ?? '') === 'CALL_ENDED') {
        return (
            <NoCallRecordFound
                connectionStatus={connectionStatus}
                subscriptionStatus={subscriptionStatus}
                showRefreshButton={showRefreshButton}
                handleRefresh={handleRefresh}
            />
        );
    }
    if (currentCall?.walkthroughData === null)
        return (
            <NoClientRecordFound
                currentCall={currentCall}
                onHubspot={onHubspot}
                connectionStatus={connectionStatus}
                subscriptionStatus={subscriptionStatus}
                showRefreshButton={showRefreshButton}
                handleRefresh={handleRefresh}
            />
        );

    if (currentCall.hookEvent === 'CALL_DIALED') {
        return (
            <DialingCall
                currentCall={currentCall}
                onNewForm={onNewForm}
                getUserData={getUserData}
                connectionStatus={connectionStatus}
                subscriptionStatus={subscriptionStatus}
                showRefreshButton={showRefreshButton}
                handleRefresh={handleRefresh}
                onClearCall={handleClearCall}
                tooltipText={currentCall.persona ? PERSONA_TOOLTIP_MAP[currentCall.persona] || undefined : undefined}
            />
        );
    }

    if (currentCall?.hookEvent === 'CALL_ANSWERED')
        return (
            <ConnectedCall
                currentCall={currentCall}
                onNewForm={onNewForm}
                getUserData={getUserData}
                connectionStatus={connectionStatus}
                subscriptionStatus={subscriptionStatus}
                showRefreshButton={showRefreshButton}
                handleRefresh={handleRefresh}
                onClearCall={handleClearCall}
                tooltipText={currentCall.persona ? PERSONA_TOOLTIP_MAP[currentCall.persona] || undefined : undefined}
            />
        );

    return (
        <NoCallRecordFound
            connectionStatus={connectionStatus}
            subscriptionStatus={subscriptionStatus}
            showRefreshButton={showRefreshButton}
            handleRefresh={handleRefresh}
        />
    );
});

export default Live;
